"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="207653" className="hidden md:flex gap-4">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={`${navigationMenuTriggerStyle()} bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent data-[state=open]:bg-transparent relative after:absolute after:bottom-2 after:left-4 after:right-4 after:h-[2px] after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100 text-foreground font-default font-medium text-sm`}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="md:hidden absolute top-full left-0 w-full bg-background border-b border-border shadow-xl py-4 px-6 flex flex-col gap-4">
      {loading ? <div className="flex flex-col gap-4">
          <div className="h-6 w-32 bg-muted animate-pulse rounded" />
          <div className="h-6 w-32 bg-muted animate-pulse rounded" />
        </div> : <nav className="flex flex-col gap-2">
          {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-foreground font-default font-medium py-3 border-b border-border/40 text-lg" onClick={() => setIsOpen(false)} data-index={i}>
              {item.label}
            </Link>)}
        </nav>}
      <div className="pt-4">
        <Button asChild className="w-full rounded-xl text-base h-12 bg-primary text-primary-foreground font-default font-semibold shadow-lg shadow-primary/20">
          <Link to="https://10percent-yield.com/investments/">INVEST WITH US</Link>
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 transition-all duration-300 dark bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border/20 shadow-md" : "bg-background"}`}>
      <MenuProvider menu_id="40">
        <header className="container mx-auto px-4 h-20 flex items-center justify-between">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1628823/img/tourmate_logo.png" alt="YieldCapital" className="h-10 w-auto object-contain" />
            </Link>
          </div>

          <div className="flex-1 flex justify-center">
            <DesktopNav />
          </div>

          <div className="flex-shrink-0 flex items-center gap-4">
            <Link to="tel:+44 (0) 20 8142 6235" className="hidden md:inline-flex items-center text-foreground font-default font-bold text-sm">
              +44 (0) 20 8142 6235
            </Link>
            <Button asChild className="hidden md:inline-flex rounded-xl px-8 font-default font-semibold shadow-lg shadow-primary/20 bg-primary text-primary-foreground hover:shadow-primary/30 transition-all duration-300">
              <Link to="https://10percent-yield.com/investments/">INVEST WITH US</Link>
            </Button>
            
            <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-white/10" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </header>

        <MobileNav isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </section>;
}